import React, { useContext, useEffect, useState } from "react";
import moment from "moment";
import { Toast } from "./../UI/toast/Toast";
import { SUCCESS_RESPONSE } from "../../helpers/codeResponses/codes";
import { fetchProperties, findOneConfig } from "../../helpers/api/condominium";
import PageHeader from "../common/PageHeader";
import handleErrorMessage from "../../helpers/errors/handleErrorMessage";
import NumberFormat from "react-number-format";
import Logo from "../navbar/Logo";
import withRedirect from "../../helpers/hoc/withRedirect";
import { useParams } from "react-router";
import {
  DebtToPayProviderDetails,
  fetchCreateToPayProvider,
  fetchDebtToPayPayments,
  fetchVoidPreview,
} from "../../helpers/api/finance";
import { useSelector } from "react-redux";
import useSWRFetchPagination from "../../helpers/hooks/useSWRFetchPagination";
import ButtonIcon from "../common/ButtonIcon";
import { Alert, Card, CardBody, Col, Modal, Row, Spinner, Table } from "reactstrap";
import SupportLink from "../root/support/components/SupportLink";
import CreateTopayProviderForm from "./CreateTopayProviderForm";
import { CurrencyContext } from "../../context/Context";
import useSWRFetch from "../../helpers/hooks/useSWRFetch";
import ItemImageTitleSubProperty from "../common/ItemImageTitleSubProperty";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBan, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import useModuleBase from "../../helpers/hooks/useModuleBase";
import VoidPaymentModal from "./VoidPaymentModal";

const CreateDebtToPay = ({ setRedirectUrl, setRedirect, handleSubmitData }) => {
  const moduleBase = useModuleBase();

  const { id, debtToPayId } = useParams();
  const { selectedCondominium } = useSelector((state) => state.condominium);
  const { condominium, address, phone } = useSelector(
    (state) => state.condominium
  );
  const [params] = useState({
    condominiumId: selectedCondominium,
  });
  const { data: dataConfig, isLoading: loadingConfig } = useSWRFetch(
    selectedCondominium,
    findOneConfig
  );
  const { currency: curr, baseCurrency } = useContext(CurrencyContext);
  const activeExchangeRate = Number(curr?.value ?? 0);
  const { role } = useSelector((state) => state.auth.user);
  const [debttopay, setDebttopay] = useState(null);
  const [collapseOne, collapseOneOpen] = useState(false);
  const [isVoidOpen, setIsVoidOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [voidBlockers, setVoidBlockers] = useState([]);
  const {
    data,
    meta,
    handler: { nextPage, previousPage, perPage, goToPage },
  } = useSWRFetchPagination(fetchProperties, params);

  useEffect(() => {
    let isMounted = true;
    if (debtToPayId) {
      const findOne = async () => {
        try {
          const { data } = await DebtToPayProviderDetails(debtToPayId);
          isMounted && setDebttopay(data);
        } catch (error) {
          handleErrorMessage(error);
        }
      };
      findOne();
    }
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line
  }, [debtToPayId, reloadKey]);

  useEffect(() => {
    let isMounted = true;
    setVoidBlockers([]);
    if (role === "manager" && debttopay && !debttopay.voided_at && !debttopay.can_void) {
      fetchVoidPreview("debt", debttopay.id)
        .then((response) => {
          if (isMounted) setVoidBlockers(response?.data?.blockers || []);
        })
        .catch(() => {});
    }
    return () => {
      isMounted = false;
    };
  }, [role, debttopay]);

  const formatDateSafe = (date) => {
    if (!date) return "";

    const raw = String(date).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const [year, month, day] = raw.split("-");
      return `${Number(day)}/${Number(month)}/${year}`;
    }

    const parsed = moment(raw);
    return parsed.isValid() ? parsed.format("D/M/YYYY") : "";
  };

  const [payments, setPayments] = useState([]);
  const [paymentsKey, setPaymentsKey] = useState(0);
  const [voidTarget, setVoidTarget] = useState(null);

  useEffect(() => {
    let isMounted = true;
    if (debtToPayId) {
      fetchDebtToPayPayments(debtToPayId)
        .then(({ data }) => isMounted && setPayments(data || []))
        .catch(handleErrorMessage);
    }
    return () => {
      isMounted = false;
    };
  }, [debtToPayId, paymentsKey]);

  const refreshAfterVoid = () => {
    setPaymentsKey((key) => key + 1);
    setReloadKey((key) => key + 1);
  };

  const create = async (values) => {
    const {
      voucher,
      debts_to_pay_id,
      description,
      reference,
      amount,
      pay_from_account_current,
      reserve_fund_id,
      bank_id,
    } = values;

    try {
      const formData = new FormData();
      formData.append("debts_to_pay_id", debts_to_pay_id);
      formData.append("description", description);
      formData.append("reference", reference);
      formData.append("amount", amount);
      formData.append("pay_from_account_current", pay_from_account_current);
      if (activeExchangeRate) formData.append("rate", activeExchangeRate);
      if (bank_id) {
        formData.append("bank_id", bank_id);
      }
      if (pay_from_account_current === 0 && !bank_id) {
        formData.append("reserve_fund_id", reserve_fund_id);
      }

      if (voucher) formData.append("voucher", voucher);

      const { code } = await fetchCreateToPayProvider(
        selectedCondominium,
        formData
      );

      if (code === SUCCESS_RESPONSE.OK) {
        setRedirectUrl(`/dashboard/${moduleBase}/${id}/topay`);
        setRedirect(true);
        Toast.fire({
          type: "success",
          title: `Pago registrado exitosamente.`,
        });
      }
    } catch (error) {
      handleErrorMessage(error);
    }
  };

  // Los bolivares de una cuenta por pagar salen de la tasa de la FECHA DEL
  // GASTO, no de la de hoy: el gasto ya ocurrio y esos son los bolivares que
  // de verdad salieron del banco ese dia. El backend ya la resuelve y manda
  // los montos convertidos, para que pantalla y reporte no se separen nunca.
  const expenseRate = debttopay?.exchange_rate ?? null;
  const expenseRateIso =
    expenseRate?.currency?.iso || curr?.currency?.iso || "Bs";

  // Sin tasa registrada hasta la fecha del gasto no se inventa ninguna: se
  // muestra solo la moneda base.
  const renderConverted = (value) =>
    expenseRate && value !== null && value !== undefined ? (
      <NumberFormat
        displayType="text"
        prefix={" | " + expenseRateIso + " "}
        allowedDecimalSeparators={true}
        decimalScale={2}
        fixedDecimalScale
        thousandSeparator={true}
        value={value}
      />
    ) : null;

  const fechaEmision = new Date(debttopay && debttopay?.created_at);
  const opcionesE = { day: "numeric", month: "numeric", year: "numeric" };
  const fechaFormateadaE = fechaEmision.toLocaleDateString(
    undefined,
    opcionesE
  );
  const voidedPayments = payments.filter((payment) => payment.voided_at);

  return (
    <>
      <PageHeader
        title="Cuenta por pagar"
        titleTag="h2"
        className="mb-3"
        col={{ lg: 9 }}
      >
        <SupportLink />
      </PageHeader>
      {debttopay && (
        <>
          <Card className="mb-3">
            <CardBody>
              <Row className="justify-content-between align-items-center">
                <Col md></Col>
                <Col xs="auto">
                  <ButtonIcon
                    color="falcon-default"
                    size="sm"
                    icon="arrow-down"
                    className="mr-2 mb-2 mb-sm-0"
                  >
                    Descargar (.pdf)
                  </ButtonIcon>
                  <ButtonIcon
                    color="falcon-default"
                    size="sm"
                    icon="print"
                    disabled
                    className="mr-2 mb-2 mb-sm-0"
                  >
                    Imprimir
                  </ButtonIcon>
                  {role === "manager" && debttopay.can_void && (
                    <ButtonIcon
                      color="falcon-danger"
                      size="sm"
                      icon={faBan}
                      className="mr-2 mb-2 mb-sm-0"
                      onClick={() => setIsVoidOpen(true)}
                    >
                      Anular cuenta
                    </ButtonIcon>
                  )}
                  {!debttopay.voided_at && debttopay.amount !== debttopay.total_paid && (
                    <ButtonIcon
                      color="falcon-success"
                      size="sm"
                      icon="dollar-sign"
                      className="mb-2 mb-sm-0"
                      /*      onClick={() =>
                      history.push({
                        pathname: `/dashboard/${moduleBase}/${id}/topay-provider/edit/${debttopay.id}/payment`,
                      })
                    } */
                      onClick={() => collapseOneOpen(!collapseOne)}
                    >
                      Emitir pago
                    </ButtonIcon>
                  )}
                </Col>
              </Row>
            </CardBody>
          </Card>
          {(debttopay.voided_at || voidedPayments.length > 0) && (
            <Alert color="danger" className="shadow-sm fs--1">
              {debttopay.voided_at && (
                <div className={voidedPayments.length > 0 ? "mb-3" : ""}>
                  <strong className="d-block">Cuenta por pagar anulada</strong>
                  {debttopay.void_reason && (
                    <span className="d-block">Motivo: {debttopay.void_reason}</span>
                  )}
                  <span className="d-block fs--2">
                    {`Anulada el ${formatDateSafe(debttopay.voided_at)}${debttopay.voided_by_name ? ` por ${debttopay.voided_by_name}` : ""}`}
                  </span>
                </div>
              )}
              {voidedPayments.length > 0 && (
                <>
                  <strong className="d-block mb-1">
                    {voidedPayments.length === 1
                      ? "Pago anulado"
                      : `Pagos anulados (${voidedPayments.length})`}
                  </strong>
                  <ul className="mb-0 pl-3">
                    {voidedPayments.map((payment, index) => (
                      <li
                        key={payment.id}
                        className={index < voidedPayments.length - 1 ? "mb-2" : ""}
                      >
                        {`${formatDateSafe(payment.payment_date || payment.created_at)}${payment.reference ? ` · Ref. ${payment.reference}` : ""}${payment.void_reason ? ` — Motivo: ${payment.void_reason}` : ""}`}
                        <span className="d-block fs--2">
                          {`Anulado el ${formatDateSafe(payment.voided_at)}${payment.voided_by_name ? ` por ${payment.voided_by_name}` : ""}`}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </Alert>
          )}
          {!debttopay.voided_at && voidBlockers.length > 0 && (
            <Alert color="info" className="shadow-sm fs--1">
              {voidBlockers.join(" ")}
            </Alert>
          )}
          <Card className="mb-3" id="ToPay">
            <CardBody>
              <ItemImageTitleSubProperty
                image={dataConfig?.data?.logo}
                title1={"Planilla de liquidación de gastos"}
                titleCondominium={`Condominio: ${condominium}`}
                titleProperty={"Datos del proveedor"}
                titleInvoice={"Datos adicionales"}
                condominium={[
                  {
                    label: "Documento de identificación",
                    value: phone || "No disponible",
                  },
                  /*     {
                        label: "Teléfono",
                        value: userData.phone || "No disponible",
                      }, */
                  { label: "Dirección", value: address },
                ]}
                property={[
                  {
                    label: "Proveedor",
                    value: `${debttopay.finance.provider === null
                      ? debttopay?.finance?.provider_name
                      : debttopay?.finance?.provider?.name || "No disponible"
                      }`,
                  },
                  {
                    label: "Descripción",
                    value: `${debttopay.finance.provider === null
                      ? debttopay?.finance?.provider_description
                      : debttopay?.finance?.provider?.description || "No disponible"
                      }`,
                  },
                  {
                    label: "Correo",
                    value: `${debttopay.finance.provider === null
                      ? debttopay?.finance?.provider_email
                      : debttopay?.finance?.provider?.email || "No disponible"
                      }`,
                  },

                  {
                    label: "Documento de identificación",
                    value: `${debttopay?.finance?.provider === null
                      ? debttopay?.finance?.provider_dni
                      : debttopay?.finance?.provider?.dni || "No disponible"
                      }`,
                  },
                  {
                    label: "Teléfono",
                    value: `${debttopay?.finance?.provider === null
                      ? debttopay?.finance?.provider_phone
                      : debttopay?.finance?.provider?.phone || "No disponible"
                      }`,
                  },
                ]}
                invoice={[
                  {
                    label: "Fecha de emisión",
                    value: debttopay ? fechaFormateadaE : "No disponible",
                  },
                  {
                    label: "Fecha del gasto",
                    value: debttopay?.expense_date
                      ? formatDateSafe(debttopay.expense_date)
                      : "No disponible",
                  },
                  {
                    // El propietario tiene que poder ver por qué el monto dio
                    // esos bolívares: sin la tasa a la vista no hay forma de
                    // cuadrarlo contra lo que salió del banco.
                    label: "Tasa de cambio",
                    value: expenseRate ? (
                      <>
                        {/* La tasa se guarda con 6 decimales y se muestra
                            entera: redondeada a dos, multiplicarla por el
                            monto no da el bolivar que dice la pantalla y el
                            propietario no puede cuadrar la cuenta. Sin
                            fixedDecimalScale para no arrastrar ceros. */}
                        <NumberFormat
                          displayType="text"
                          prefix={`${expenseRateIso} `}
                          suffix={` por ${baseCurrency.symbol} 1`}
                          allowedDecimalSeparators={true}
                          decimalScale={6}
                          thousandSeparator={true}
                          value={expenseRate.value}
                        />
                        {!expenseRate.is_exact && (
                          <span className="d-block text-700 fs--2">
                            Última tasa registrada antes del gasto (
                            {formatDateSafe(expenseRate.date)})
                          </span>
                        )}
                      </>
                    ) : (
                      "No disponible"
                    ),
                  },
                  {
                    label: "Estatus",
                    // `paymented` llega como booleano, no como 1.
                    value: debttopay.voided_at
                      ? "Anulada"
                      : debttopay.paymented
                        ? "Aprobado"
                        : "Pendiente",
                  },

                  {
                    label: "Monto abonado",
                    value: (
                      <>
                        <NumberFormat
                          displayType="text"
                          prefix={`${baseCurrency.symbol} `}
                          allowedDecimalSeparators={true}
                          decimalScale={2}
                          fixedDecimalScale
                          thousandSeparator={true}
                          value={debttopay.total_paid}
                        />
                        {renderConverted(debttopay.total_paid_converted)}
                      </>
                    ),
                  },
                  {
                    label: "Monto pendiente",
                    value: (
                      <>
                        <NumberFormat
                          displayType="text"
                          prefix={`${baseCurrency.symbol} `}
                          allowedDecimalSeparators={true}
                          decimalScale={2}
                          fixedDecimalScale
                          thousandSeparator={true}
                          value={debttopay.amount - debttopay.total_paid}
                        />
                        {renderConverted(debttopay.pending_amount_converted)}
                      </>
                    ),
                  },
                  ...(debttopay?.paymented
                    ? [
                      {
                        label: "Fecha de pago",
                        value: debttopay?.payment_date
                          ? formatDateSafe(debttopay?.payment_date)
                          : debttopay?.created_at
                            ? formatDateSafe(debttopay?.created_at)
                            : "",
                      },
                    ]
                    : []),
                ]}
                loading={loadingConfig}
              />

              <div className="table-responsive fs--1">
                <Table striped className="border-bottom">
                  <thead>
                    <tr /* className="bg-primary text-white" */>
                      <th className="border-0">Cuenta</th>
                      <th className="border-0 align-middle">Descripción</th>
                      <th className="border-0 text-right">Monto</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="align-middle" style={{ width: "40%" }}>
                        <h6 className="mb-0">
                          {debttopay?.name
                            ? debttopay?.name
                            : debttopay.finance.name}
                        </h6>
                      </td>
                      <td className="align-middle" style={{ width: "40%" }}>
                        <h6 className="mb-0">
                          {debttopay?.description
                            ? debttopay?.description
                            : debttopay.finance.description}
                        </h6>
                      </td>
                      {/*      <td className="align-middle text-center">{val.voucher ? <Button color='link' size='sm' onClick={() => { setIsOpen(true); setIndex(i) }}>Ver Factura</Button> : <>No agregada</>}
                                            {isOpen &&
                                                <Lightbox
                                                    reactModalStyle={{ zIndex: 1250 }}
                                                    mainSrc={images[index]}
                                                    nextSrc={images[(index + 1) % images.length]}
                                                    prevSrc={images[(index + images.length - 1) % images.length]}
                                                    onCloseRequest={() => setIsOpen(false)}
                                                    onMovePrevRequest={() => setIndex((index + images.length - 1) % images.length)}
                                                    onMoveNextRequest={() => setIndex((index + 1) % images.length)}
                                                />
                                            }</td> */}

                      <td
                        className="align-middle text-right"
                        style={{ width: "20%" }}
                      >
                        <span className="d-block font-weight-semi-bold">
                          <NumberFormat
                            className="text-right text-nowrap"
                            displayType="text"
                            allowedDecimalSeparators={true}
                            thousandSeparator={true}
                            prefix={`${baseCurrency.symbol} `}
                            fixedDecimalScale
                            decimalScale={2}
                            value={debttopay.amount}
                          />
                        </span>
                        {expenseRate && (
                          <span className="d-block text-700 fs--2 text-nowrap">
                            <NumberFormat
                              className="text-right text-nowrap"
                              displayType="text"
                              allowedDecimalSeparators={true}
                              thousandSeparator={true}
                              decimalScale={2}
                              fixedDecimalScale
                              prefix={expenseRateIso + " "}
                              value={debttopay.amount_converted}
                            />
                          </span>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>
              <Row noGutters className="justify-content-end">
                <Col xs="auto">
                  <Table size="sm" borderless className="fs--1 text-right">
                    <tbody>
                      <tr className="border-top">
                        <th className="text-900">Total:</th>
                        <td className="font-weight-semi-bold">
                          <span className="d-block">
                            <NumberFormat
                              className="text-right text-nowrap"
                              displayType="text"
                              allowedDecimalSeparators={true}
                              thousandSeparator={true}
                              fixedDecimalScale
                              decimalScale={2}
                              prefix={`${baseCurrency.symbol} `}
                              value={debttopay.amount}
                            />
                          </span>
                          {expenseRate && (
                            <span className="d-block text-700 fs--2 text-nowrap">
                              <NumberFormat
                                className="text-right text-nowrap"
                                displayType="text"
                                allowedDecimalSeparators={true}
                                thousandSeparator={true}
                                decimalScale={2}
                                fixedDecimalScale
                                prefix={expenseRateIso + " "}
                                value={debttopay.amount_converted}
                              />
                            </span>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Col>
              </Row>

              {payments.length > 0 && (
                <div className="table-responsive fs--1 mt-3">
                  <h6 className="mb-2">Pagos de esta cuenta</h6>
                  <Table size="sm" className="border-bottom">
                    <thead>
                      <tr>
                        <th className="border-0">Fecha</th>
                        <th className="border-0">Referencia</th>
                        <th className="border-0 text-right">Monto</th>
                        <th className="border-0">Estado</th>
                        <th className="border-0 d-print-none" />
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((payment) => (
                        <tr key={payment.id}>
                          <td className="text-nowrap">
                            {formatDateSafe(payment.payment_date || payment.created_at)}
                          </td>
                          <td>{payment.reference || "—"}</td>
                          <td className="text-right">
                            <NumberFormat
                              className="text-nowrap"
                              displayType="text"
                              thousandSeparator={true}
                              prefix={`${baseCurrency.symbol} `}
                              fixedDecimalScale
                              decimalScale={2}
                              value={payment.amount}
                            />
                          </td>
                          <td>
                            {payment.voided_at ? (
                              <span className="text-600">Anulado</span>
                            ) : (
                              "Pago realizado"
                            )}
                          </td>
                          <td className="text-right d-print-none">
                            {!payment.voided_at && (
                              <button
                                type="button"
                                className="btn btn-link btn-sm text-danger p-0"
                                onClick={() => setVoidTarget(payment.id)}
                              >
                                Anular pago
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}

              {debttopay?.voucher && debttopay?.voucher !== "false" && (
                <Row noGutters className="justify-content-start mt-3">
                  <Col xs="auto">
                    <Table size="sm" borderless className="fs--1 text-left">
                      <thead>
                        <tr>
                          <th className="border-0">Comprobante de pago</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            <div className="App mt-3">
                              <img
                                src={debttopay?.voucher}
                                style={{
                                  height: "400px",
                                  width: "400px",
                                  maxHeight: "400px",
                                  maxWidth: "400px",
                                  objectFit: "contain",
                                }}
                                alt="Comprobante de pago"
                              />
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </Col>
                </Row>
              )}
              {loadingConfig ? (
                <Spinner />
              ) : (
                <div className="text-start text-muted mt-3 pt-1 mb-0">
                  <Logo
                    to={null}
                    styleImage={{
                      width: "100px",
                    }}
                    className={"mb-2"}
                  />
                  <div className="mb-0">
                    {dataConfig?.data?.note !== "" && (
                      <FontAwesomeIcon
                        icon={faInfoCircle}
                        className="mr-2"
                        size="sm"
                      />
                    )}
                    <pre
                      style={{
                        fontFamily: "Open Sans",
                        fontSize: ".83333rem",
                        color: "#5e6e82",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {dataConfig?.data.note}
                    </pre>
                  </div>
                </div>
              )}
            </CardBody>
            {/*  <Modal isOpen={collapseOne} data-testid="modal">
                    <ModalHeader>Confirmación</ModalHeader>
                    <ModalBody>
                        <Col data-testid="form" tag={Form} className="bg-light" onSubmit={handleSubmit(markaspaid)} >
                            <Row form className="py-2">
                                <Label>Está apunto de marcar como pagada esta cuenta por pagar</Label>
                            </Row>
                            {payment_proof && <Row form>
                                <Col style={{ backgroundImage: `url(${payment_proof})`, backgroundPosition: 'center', backgroundSize: 'contain', height: '400px', width: '100%', maxHeight: '400px', backgroundRepeat: 'no-repeat' }}>

                                </Col>
                            </Row>}
                            <Row form>
                                <Col tag={FormGroup}>
                                    <Label>Prueba de pago</Label>
                                    <Input type='file' name="payment_proof" id="payment_proof" accept="image/png, image/jpeg" onChange={(event) => {
                                        setPayment_proof(URL.createObjectURL(event.target.files[0]));
                                        register('payment_proof');
                                        setValue('payment_proof', event.target.files[0]);
                                    }} placeholder='Prueba de pago' />
                                </Col>
                            </Row>
                            <Row form className="justify-content-end">
                                <Button className="mr-2" onClick={() => collapseOneOpen(!collapseOne)}>
                                    Cancelar
                                </Button>
                                <Button
                                    data-testid="submit-button"
                                    color="primary"
                                    disabled={Object.keys(errors).length > 0}
                                    type='submit'
                                >Sí, marcar como pagada</Button>
                            </Row>
                        </Col>
                    </ModalBody>
                </Modal> */}
          </Card>
        </>
      )}

      <VoidPaymentModal
        isOpen={isVoidOpen}
        toggle={() => setIsVoidOpen(false)}
        kind="debt"
        paymentId={debttopay?.id}
        onVoided={() => setReloadKey((key) => key + 1)}
      />
      <>
        {" "}
        <Modal isOpen={collapseOne} size="lg" data-testid="modal">
          <CreateTopayProviderForm
            handler={{ nextPage, previousPage, perPage, goToPage, meta }}
            providers={data}
            condominiumId={selectedCondominium}
            handleSubmitData={create}
            debttopayId={debtToPayId}
            debttopay={debttopay}
          />
        </Modal>
      </>
      <VoidPaymentModal
        isOpen={voidTarget !== null}
        toggle={() => setVoidTarget(null)}
        kind="provider"
        paymentId={voidTarget}
        onVoided={refreshAfterVoid}
      />
    </>
  );
};

export default withRedirect(CreateDebtToPay);
